import React from "react";
import { Row, Col, Button, Steps } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import "./Application1.css";
import "../components/StepsTracker.css";

const Application1: React.FC = () => {
  return (
    <div className="application-step-container">
      <div className="application-title-card">
        <div className="title-card-content">
          <div className="city-name-title">City of New Orleans, Louisiana</div>
          <h1 className="program-title-main">E-Bike Incentive Application</h1>
        </div>
      </div>

      <div className="progress-tracker-container">
        <Steps
          current={0}
          className="progress-tracker-steps"
          items={[
            { title: "Review Eligibility" },
            { title: "Confirm Details" },
            { title: "Personal Information" },
            { title: "Upload Documents" },
            { title: "Income Verification" },
            { title: "Survey" },
            { title: "Future Communications" },
            { title: "Terms & Conditions" },
          ]}
        />
      </div>

      <div className="main-step-card">
        <div className="step-header-container">
          <div className="step-badge">
            <div className="step-label">STEP</div>
            <div className="step-number">1</div>
          </div>
          <div className="step-content-info">
            <h2 className="step-title">
              Review Eligibility, E-bike Qualifications, & Purchasing Rules
            </h2>
            <p className="step-description">
              Please review the following eligibility requirements, e-bike
              qualifications, purchasing rules, and required documents.
            </p>
          </div>
        </div>

        <div className="step-divider" />

        <div className="requirements-section">
          <Row gutter={[0, 20]}>
            <Col span={24}>
              <div className="requirement-card">
                <h3 className="requirement-title">Eligibility Requirements</h3>
                <div className="requirement-content">
                  Must be age 18 and above
                  <br />
                  Must have a household income of under $150,000 per year
                  <br />
                  Must be a resident of the City of New Orleans
                  <br />
                  Only one e-bike per household can be awarded.
                  <br />
                  Employees of the City of New Orleans are not eligible for the
                  program.
                </div>
              </div>
            </Col>

            <Col span={24}>
              <div className="requirement-card">
                <h3 className="requirement-title">
                  E-Bike Qualifications & Purchasing Rules
                </h3>
                <div className="requirement-content">
                  <ul className="requirement-list">
                    <li>New</li>
                    <li>Class 1, 2 or 3</li>
                    <li>
                      Minimum retail cost of $999 (e-bikes below $999 are not
                      eligible for this program) This is to manage quality and
                      ensure access to maintenance.
                    </li>
                    <li>
                      The purchased e-bike must be for use by the e-bike
                      incentive applicant and not for resale. Incentive cannot
                      be used on a past purchase.
                    </li>
                    <li>
                      E-bike must be purchased in person through a participating
                      local bike retailer. Incentives cannot be used online or
                      with non-participating stores. You may check the list of
                      participating local bike shops{" "}
                      <a
                        href="https://staging-columbusebikeincentives.bp.solutions/participating-shops"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-link"
                      >
                        here
                      </a>
                      .
                    </li>
                    <li>
                      Bicycles must be purchased at full MAP (the lowest price a
                      retailer is allowed to advertise a product for sale, as
                      set by the manufacturer). Incentive codes may not be
                      combined with any other discounts.
                    </li>
                    <li>
                      All sales are final. You may not return or exchange goods
                      after the incentive code has been processed.
                    </li>
                  </ul>
                </div>
              </div>
            </Col>

            <Col span={24}>
              <div className="requirement-card">
                <h3 className="requirement-title">
                  Documents Required for Residency Verification
                </h3>
                <p className="requirement-intro">
                  You will need access to <span className="bold-text">one</span>{" "}
                  of the following documents to demonstrate residency in
                  New Orleans.
                </p>
                <Row gutter={20} className="document-columns">
                  <Col xs={24} md={14}>
                    <div className="document-list">
                      Louisiana Driver's license, Learner's Permit or
                      Identification Card
                      <br />
                      Bank Statement
                      <br />
                      Child Support Statement or Account Summary from Louisiana
                      Department of Job and Family Services
                      <br />
                      Federal or Louisiana Income Tax Return Filing
                      <br />
                      Court Order Of Probation, Parole, Or Mandatory Release
                      <br />
                      Renter, Homeowner, Life, Or Automobile Insurance Policy Or
                      Card
                      <br />
                      Credit Card Statement
                      <br />
                      Mortgage Account Statement
                      <br />
                      Proof of Home Ownership (Property Deed, Property Tax Bill
                      or Auditor Tax Statement)
                    </div>
                  </Col>
                  <Col xs={24} md={10}>
                    <div className="document-list">
                      Social Security Administration Document
                      <br />
                      Electric Bill
                      <br />
                      Telephone Bill
                      <br />
                      Water Bill
                      <br />
                      Sewer Bill
                      <br />
                      Cable Bill
                      <br />
                      Satellite Bill
                      <br />
                      Heating Oil Bill
                      <br />
                      Propane Provider Bill
                      <br />
                      Gas Bill
                      <br />
                      Public Assistance Benefits Statement
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>

            <Col span={24}>
              <div className="requirement-card">
                <h3 className="requirement-title">
                  Documents Required for Income Verification
                </h3>
                <p className="requirement-intro">
                  You will need access to <span className="bold-text">one</span>{" "}
                  of the following documents to demonstrate income eligibility.
                </p>
                <div className="document-list">
                  2023 Federal tax form 1040 (First page only)
                  <br />
                  W-2 Wage and Tax Statement (If you are in a dual income
                  household, upload W-2 documents for both earners)
                  <br />
                  Pay Stub (within the past 3 months) ( If you are in a dual
                  income household, upload W-2 documents for both earners)
                  <br />
                  Benefit Letter from Social Security
                  <br />
                  Pension Award Statement
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <div className="navigation-buttons">
        <Link to="/">
          <Button
            type="default"
            icon={<ArrowLeftOutlined />}
            className="back-button"
          >
            Back
          </Button>
        </Link>
        <Link to="/application2">
          <Button type="primary" className="next-button" size="large">
            Next
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default Application1;
